import { Input, Select } from 'antd';
import React from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { AsyncTypeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import { withTranslation, WithTranslation } from 'react-i18next';
import Contact from '../../../../Model/models/contactModel';
import Location from '../../../../Model/models/locationModel';
import Order from '../../../../Model/models/orderModel';
import Shipping from '../../../../Model/models/shippingModel';
import ProductType from '../../../../Model/productType';
import { ContactsService } from '../../../../services/contactsService';
import { LocationsService } from '../../../../services/locationsService';

interface OrderFormShippingModalProps extends WithTranslation {
  productTypes: ProductType[],
  showModal: boolean,
  shippings: any[],
  order: Order,
  onClose: () => void
  onSave: (shippings: Shipping[]) => void
}

interface OrderFormShippingModalState {
  location?: Location,
  contact?: Contact,
  showModal: boolean,
  isSearchLoading: boolean,
  contacts: Contact[],
  newContact?: Contact,
  locations: Location[],
  newLocation?: Location,
  order?: Order,
  newShippings: Shipping[]
}

interface ResidualProduct {
  productTypeId?: number;
  qty: number;
  residual: number;
  friendlyName?: string;
}

class OrderFormShippingModal extends React.Component<OrderFormShippingModalProps, OrderFormShippingModalState> {

  contactsService: ContactsService = new ContactsService();
  locationsService: LocationsService = new LocationsService();

  constructor(props: OrderFormShippingModalProps) {
    super(props);
    this.state = {
      showModal: true,
      isSearchLoading: false,
      contacts: [],
      locations: [],
      newShippings: []
    };
  }

  shouldComponentUpdate(nextProps: Readonly<OrderFormShippingModalProps>) {
    if (nextProps.order !== this.state.order) {
      this.setState({ order: nextProps.order });
    }
    if (nextProps.showModal !== this.state.showModal) {
      this.setState({ showModal: nextProps.showModal });
    }
    return true;
  }

  searchContactComponent() {
    const self = this;
    return <>
      <Form.Label>{this.props.t('SEARCH_CONTACT')}</Form.Label>
      <AsyncTypeahead
        id={'search-shipping-contact'}
        isLoading={this.state.isSearchLoading}
        labelKey={'mail'}
        key={'id'}
        delay={400}
        renderMenu={(results, menuProps, state) => (
          <Menu {...menuProps}>
            {results.map((result, index) => (
              <MenuItem key={index} option={result} position={index}>
                {`${result.mail} (${result.name} ${result.surname})`}
              </MenuItem>
            ))}
            {
              results.length === 0 &&
              <div style={{ marginLeft: '1em', cursor: 'pointer' }} onClick={() => {
                const contact = new Contact();
                contact.mail = state.text;
                self.setState({
                  contact: undefined,
                  newContact: contact,
                  locations: []
                });
              }}>
                {self.props.t('ADD_CONTACT')}
              </div>
            }
          </Menu>
        )}
        onSearch={(query) => {
          this.setState({ isSearchLoading: true });
          this.contactsService.getContactsByEmail(query)
            .then(contacts => {
              self.setState({
                isSearchLoading: false,
                contacts,
                location: undefined,
                newLocation: undefined
              });
            });
        }}
        onChange={async (selected) => {
          const contact = selected[0];
          if (contact) {
            const locations = await this.locationsService.getLocationsByEmail(contact.mail);
            this.setState({ contact, locations });
          }
        }}
        options={this.state.contacts}
      />
    </>;
  }

  getResidualItems() {
    const { order } = this.state;
    if (order) {
      const noRefundOrderProducts = order.orderProducts.filter(op => {
        const productType = this.props.productTypes.find(pt => pt.id === op.product_type_id);
        return !op.status && op.product_type_id && productType && !productType.code.startsWith('LL_');
      });
      const residualProducts: ResidualProduct[] = [];
      for (let nrop of noRefundOrderProducts) {
        let residualProduct = residualProducts.find(rp => rp.productTypeId === nrop.product_type_id);
        if (!residualProduct) {
          residualProduct = {
            productTypeId: nrop.product_type_id,
            qty: 0,
            residual: 0,
            friendlyName: nrop.friendly_name
          };
          residualProducts.push(residualProduct);
        }
        residualProduct.qty += nrop.quantity;
        residualProduct.residual += nrop.quantity;
      }
      for (let shipping of order.shippings) {
        let residualProduct = residualProducts.find(rp => rp.productTypeId === shipping.sensors_type_id);
        if (residualProduct) {
          residualProduct.residual -= shipping.quantity;
        }
      }
      return residualProducts.filter(rp => rp.residual > 0);
    }
    return [];
  }

  getLocationsSection() {
    const { newLocation } = this.state;
    return <>
      <div style={{ marginTop: '1em' }}>
        <Form.Label>{this.props.t('LOCATIONS_LIST')}</Form.Label>
        <Select style={{ width: '100%' }}
          defaultValue={this.state.location?.id}
          onChange={(value) => {
            if (value) {
              const location = this.state.locations.find(l => l.id == value);
              this.setState({ location, newLocation: undefined });
              return;
            }
            this.setState({ location: undefined, newLocation: new Location() });
          }}>
          {this.state.locations.map(l => <Select.Option
            key={l.id}
            value={l.id as number}
          >
            {l.address}, {l.city} - {l.country}
          </Select.Option>)}
          <Select.Option value={''}>{this.props.t('ADD_LOCATION')}</Select.Option>
        </Select>
      </div>
      {
        newLocation &&
        <>
          <hr />
          <div style={{ marginTop: '1em' }}>
            <Form.Label>{this.props.t('NEW_LOCATION')}</Form.Label>
            <Row>
              <Col sm={6}>
                <Form.Group controlId={'address'}>
                  <Form.Label>{this.props.t('ADDRESS')}</Form.Label>
                  <Form.Control required type='text' name='address'
                    defaultValue={newLocation?.address}
                    onChange={(e) => {
                      newLocation.address = e.target.value;
                      this.setState({ newLocation });
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group controlId={'city'}>
                  <Form.Label>{this.props.t('CITY')}</Form.Label>
                  <Form.Control required type='text' name='city' defaultValue={newLocation?.city}
                    onChange={(e) => {
                      newLocation.city = e.target.value;
                      this.setState({ newLocation });
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={3}>
                <Form.Group controlId={'postalCode'}>
                  <Form.Label>{this.props.t('Postal Code')}</Form.Label>
                  <Form.Control required type='text' name='postalcode'
                    defaultValue={newLocation?.postcode}
                    onChange={(e) => {
                      newLocation.postcode = e.target.value;
                      this.setState({ newLocation });
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Group controlId={'province'}>
                  <Form.Label>{this.props.t('Province')}</Form.Label>
                  <Form.Control required type='text' name='province' defaultValue={newLocation?.province}
                    onChange={(e) => {
                      newLocation.province = e.target.value;
                      this.setState({ newLocation });
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group controlId={'country'}>
                  <Form.Label>{this.props.t('COUNTRY')}</Form.Label>
                  <Form.Control required type='text' name='country' defaultValue={newLocation?.country}
                    onChange={(e) => {
                      newLocation.country = e.target.value;
                      this.setState({ newLocation });
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            {/* <AddressForm index={1} onChangeValue={this.updateNewLocation} /> */}
          </div>
        </>
      }
    </>;
  }

  addShipping(residualProduct: ResidualProduct, qty: number) {
    let shipping = this.state.newShippings.find(ns => ns.sensors_type_id === residualProduct.productTypeId);
    if (!shipping) {
      shipping = {
        quantity: 0,
        order_id: this.state.order?.id,
        sensors_type_id: residualProduct.productTypeId,
        location: this.state.location || this.state.newLocation as Location,
        location_id: this.state.location?.id || this.state.newLocation?.id
      };
      this.state.newShippings.push(shipping);
    }
    shipping.quantity = qty;
  }

  canSave(): boolean {
    const { contact, location, newLocation, newContact } = this.state;
    if ((!contact && !newContact) || (!location && !newLocation)) {
      return false;
    }
    if (
      newLocation &&
      (!newLocation.address ||
        !newLocation.city ||
        !newLocation.country ||
        !newLocation.postcode ||
        !newLocation.province)
    ) {
      return false;
    }
    if (newContact && !newContact.mail) {
      return false;
    }
    return true;
  }

  save = () => {
    const { newContact, newLocation } = this.state;
    if ((this.state.contact || newContact) && (this.state.location || newLocation)) {
      const location = this.state.location || newLocation;
      const locationContact = this.state.contact || newContact;
      if (location && locationContact) {
        location.contact_id = locationContact.id;
        this.state.newShippings.forEach(ns => {
          ns.location = location;
          ns.location_id = location?.id;
          if (ns.location) {
            ns.location.contact = locationContact;
            ns.location.contact_id = locationContact?.id;
          }
        });
        this.props.onSave(this.state.newShippings);
        this.reset();
      }
    }
  };

  reset(callback: () => void = () => { }) {
    this.setState({
      showModal: false,
      isSearchLoading: false,
      contacts: [],
      locations: [],
      location: undefined,
      newShippings: [],
      contact: undefined,
      newContact: undefined,
      newLocation: undefined
    }, callback);
  }

  render() {
    const { showModal, contact, newContact } = this.state;
    const residualProducts = this.getResidualItems();
    return <Modal
      show={showModal}
      onHide={() => {
        this.reset(() => {
          this.props.onClose();
        });
      }}>
      <Modal.Header closeButton>
        <Modal.Title>{this.props.t('SHIPPING_DATA')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col>
            <Form.Label>{this.props.t('ITEMS_FOR_SHIPPING')}</Form.Label>
            <table className="items">
              <tbody>
                {
                  residualProducts.map(residualProduct => {
                    return <tr key={residualProduct.productTypeId}>
                      <td>{residualProduct.friendlyName}</td>
                      <td>
                        <Input type={'number'}
                          defaultValue={0}
                          max={residualProduct.residual}
                          min={0}
                          onChange={(e) => {
                            this.addShipping(residualProduct, parseInt(e.target.value));
                          }}
                        />
                      </td>
                    </tr>;
                  })
                }
                {
                  residualProducts.length === 0 &&
                  <tr >
                    <td>{this.props.t('NO_PRODUCT_TO_SEND')}</td>
                  </tr>
                }
              </tbody>
            </table>
            <hr />
          </Col>
        </Row>
        {
          residualProducts.length > 0 &&
          <Row>
            <Col>
              {this.searchContactComponent()}

              <hr />
              {
                (contact || newContact) &&
                <>
                  <div style={{ marginTop: '1em' }}>
                    <Form.Label>{this.props.t('CONTACT')}</Form.Label>
                    {
                      contact &&
                      <table id='contact-table'>
                        <tbody>
                          <tr>
                            <th>{this.props.t('NAME')} {this.props.t('SURNAME')}</th>
                            <td>{contact?.name} {contact?.surname}</td>
                          </tr>
                          <tr>
                            <th>{this.props.t('EMAIL')}</th>
                            <td>{contact?.mail}</td>
                          </tr>
                          <tr>
                            <th>{this.props.t('PHONE')}</th>
                            <td>{contact?.phone}</td>
                          </tr>
                        </tbody>
                      </table>
                    }
                    {
                      newContact &&
                      <table id='contact-table'>
                        <tbody>
                          <tr>
                            <th>{this.props.t('NAME')}</th>
                            <td>
                              <Input defaultValue={newContact.name} onChange={(e) => {
                                newContact.name = e.target.value;
                                this.setState({ newContact });
                              }}></Input>
                            </td>
                          </tr>
                          <tr>
                            <th>{this.props.t('SURNAME')}</th>
                            <td>
                              <Input defaultValue={newContact.surname} onChange={(e) => {
                                newContact.surname = e.target.value;
                                this.setState({ newContact });
                              }}></Input>
                            </td>
                          </tr>
                          <tr>
                            <th>{this.props.t('EMAIL')}</th>
                            <td>
                              <Input defaultValue={newContact.mail} onChange={(e) => {
                                newContact.mail = e.target.value;
                                this.setState({ newContact });
                              }}></Input>
                            </td>
                          </tr>
                          <tr>
                            <th>{this.props.t('PHONE')}</th>
                            <td>
                              <Input defaultValue={newContact.phone} onChange={(e) => {
                                newContact.phone = e.target.value;
                                this.setState({ newContact });
                              }}></Input>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    }
                  </div>
                  <hr />
                  <hr />
                  {/* LOCATION SECTION */}
                  {this.getLocationsSection()}
                </>
              }
            </Col>
          </Row>
        }
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={!this.canSave()} onClick={this.save}>
          {this.props.t('SAVE')}
        </Button>
      </Modal.Footer>
    </Modal>;
  }

}

export default withTranslation()(OrderFormShippingModal);